import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Legal from './Components/Legal';
import MainNav from '../Common/MainNav';
import charmhealthlogo_placeholder from '../../assets/charmhealthlogo_placeholder.jpg';
import { isUserSignedIn, fetchProductDetails, fetchReviews, ssoCreateOrder, otpCreateOrder, sendOTP } from '../API/API_Handler';
import SubmitRequestModal from './Components/SubmitRequestModal';
import SocialLinks from './Components/SocialLinks';
import ImageCarousel from './Components/ImageCarousel';
import Reviews from './Components/Reviews';

const styles = {
    container: 'hidden w-full md:flex flex-col bg-marketplaceBackground overflow-x-hidden',
    banner: 'w-full h-[320px] border-b-[1px] border-exploreCategoriesBackground relative',
    backBtn: 'absolute top-20 left-4 h-fit text-white text-lg xl:text-xl font-semibold px-4 xl:px-5 py-2 xl:py-3 bg-link hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full',
    logoContainer: 'bg-white absolute w-56 h-56 -mt-50 border-[1px] border-exploreCategoriesBackground  shadow-md object-cover shrink-0 z-5',
    logoImage: 'w-full h-full object-contain',
    content: 'w-full flex flex-col items-center',
    mainContent: 'w-[85%] mx-auto mt-[6rem] flex md:flex-row xl:flex-row justify-between',
    title: 'max-w-[40rem] text-5xl md:text-6xl xl:text-7xl font-[600] mt-5 font-proximanova text-[#505050]',
    buttonGroup: 'w-[26rem] mt-12 flex justify-between',
    submitBtn: 'h-fit text-white text-lg xl:text-xl font-bold px-4 xl:px-1 py-2 xl:py-3 bg-link hover:text-integrationRequestTextHover hover:bg-charmHover w-fit mx-auto rounded-full',
    requestBtnContainer: 'w-56 xl:w-60 flex flex-row justify-center z-10',
    visitVendorBtn: 'h-fit px-4 xl:px-5 py-2 xl:py-3 rounded-full bg-link text-white hover:text-integrationRequestTextHover hover:bg-charmHover text-lg xl:text-xl font-semibold whitespace-nowrap',
    vendorInfoSection: 'pt-10 w-[85%] flex justify-between py-6 mx-auto border-b-2 border-exploreCategoriesBackground',
    vendorDetails: '',
    detailLabel: 'text-lg 2xl:text-xl text-center font-allroundgothic font-[600] charm-purple',
    detailValue: 'text-xl md:text-4xl 2xl:text-3xl w-[25rem] text-center font-[600] text-[#505050]',
    carouselContainer: 'pt-12 w-[85%] flex py-4 bg-marketplaceBackground mx-auto',
    section: 'flex flex-col w-[85%] py-10 bg-marketplaceBackground mx-auto',
    sectionHeading: 'text-sectionLabel font-bold text-xl md:text-2xl 2xl:text-3xl font-all-round-gothic-bold',
    subHeading: 'mt-4 text-xl md:text-2xl 2xl:text-3xl text-subSectionLabel font-all-round-gothic-bold',
    sectionContent: 'mt-1 text-descriptionContent text-md md:text-lg 2xl:text-xl charm-gray',
    greenText: 'text-darkgreen',
    separator: 'w-[85%] h-[2px] bg-exploreCategoriesBackground mx-auto',
    confirmationModal: 'fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center',
    confirmationBox: 'w-[40rem] bg-white p-6 shadow-lg',
    confirmationHeading: 'text-xl md:text-2xl 2xl:text-3xl font-semibold mb-4',
    confirmationMessage: 'mb-4 text-center text-md md:text-lg 2xl:text-xl font-light',
    returnButton: 'px-4 py-2 bg-exploreNavBackgroundSelected text-exploreNavTextSelected rounded-full mr-2',
    reviewsContent: 'w-[85%] mx-auto flex md:flex-row xl:flex-row justify-between'
};

const DetailedListing = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // State variables
    const [productId, setProductId] = useState('');
    const [product, setProduct] = useState(null);
    const [isSignedIn, setIsSignedIn] = useState(false);
    const [videos, setVideos] = useState([]);
    const [imageUrls, setImageUrls] = useState([]);
    const [slides, setSlides] = useState([]);
    const [userEmail, setUserEmail] = useState('');
    const [screenLoading, setLoading] = useState(false);
    const [productReviews, setProductReviews] = useState([]);
    const [otpEmail, setOtpEmail] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [optLoading, setOtpLoading] = useState(false);
    const [orderRequestLoading, setOrderRequestLoading] = useState(false);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isFeaturesExpanded, setIsFeaturesExpanded] = useState(false);

    const calculateProductRating = (reviews) => {
        if (reviews.length === 0) {
            return "No reviews yet";
        }

        const totalRating = reviews.reduce((acc, review) => acc + review.rating, 0);
        const averageRating = totalRating / reviews.length;
        return averageRating.toFixed(1);
    };

    useEffect(() => {
        document.documentElement.style.scrollBehavior = 'auto';
        document.body.style.scrollBehavior = 'auto';
        window.scrollTo(0, 0);
        const url = window.location.href;
        const extracted_productId = url.substring(url.lastIndexOf('/') + 1);
        setProductId(extracted_productId);

        // Check if user is signed in
        const user = isUserSignedIn();
        if (user) {
            setIsSignedIn(true);
        }
    }, []);

    useEffect(() => {
        if (location.state?.openModal) {
            setIsModalOpen(true);
        }
    }, [location.state]);

    useEffect(() => {
        if (productId) {
            fetchData();
        }
    }, [productId]);

    useEffect(() => {
        const videoSlides = videos.map((url) => ({ type: 'video', url }));
        const imageSlides = imageUrls.map((url) => ({ type: 'image', url }));
        setSlides([...videoSlides, ...imageSlides]);
    }, [videos, imageUrls]);

    async function _fetchReviews() {
        try {
            const _reviewsData = await fetchReviews(productId);
            setProductReviews(_reviewsData);

            //lets set product rating too
            const productRating = calculateProductRating(_reviewsData);
            setProduct((prevProduct) => ({ ...prevProduct, rating: productRating }));
        } catch (error) {
            setProductReviews([]);
            console.error('Error fetching reviews:', error);
        }
    }

    async function fetchData() {
        try {
            setLoading(true);
            const fetchListing = await fetchProductDetails(productId);
            if (!fetchListing.length) {
                navigate('/explore/all');
                return null;
            }
            //fetching and setting reviews
            _fetchReviews();

            setProduct(fetchListing[0]);
            setVideos(fetchListing[0].video_urls.split(','));
            setImageUrls(fetchListing[0].image_urls.split(','));
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const handleSendOtp = async () => {
        if (!otpEmail) {
            return;
        }
        try {
            setOtpLoading(true);
            await sendOTP(otpEmail);
            setOtpLoading(false);
        } catch (error) {
            setOtpLoading(false);
        }
    };

    //For SSO login
    const handleLogin = () => {
        const loginUrl = 'https://accounts.charmtracker.com/oauth/v2/auth?client_id=1000.RB6DWOYZJ54A25SZPTB5D3P8DQP0SQ&redirect_uri=https://charmhealthhub.com/authenticating&scope=openid+email+charmhealth.user.addons.READ+profile&response_type=token';
        window.location.href = loginUrl;
    };

    //For orders
    const handleSubmit = async (event) => {
        setOrderRequestLoading(true);
        event.preventDefault();
        let name;
        let message;
        let email;
        let practiceName;
        let otp;

        if (isSignedIn) {
            name = sessionStorage.getItem('name') || null;
            email = sessionStorage.getItem('email') || null;
            practiceName = event.target.practiceName.value || null;
        } else {
            name = event.target.name.value;
            email = event.target.email.value;
            practiceName = event.target.practiceName.value || null;
            otp = event.target.otp.value;
        }

        message = event.target.message.value;
        setUserEmail(email);

        let response = null;

        if (isSignedIn) {
            response = await ssoCreateOrder(practiceName, message, product.id, product.vendor.id);
        } else {
            response = await otpCreateOrder(name, email, otp, practiceName, message, product.id, product.vendor.id);
        }

        if (response) {
            setOrderRequestLoading(false);
            handleCloseModal();
            setIsConfirmationModalOpen(true);
        }
        else {
            setOrderRequestLoading(false);
            alert('Error submitting request. Please try again later.');
        }
    };
    const handleGoBack = () => navigate(-1);
    const handleButtonClick = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    function getFormattedUrl(url) {
        if (!url) {
            // In case product?.website is empty or undefined
            return "#";
        }

        // Check if it already starts with http:// or https://
        if (/^https?:\/\//i.test(url)) {
            return url;
        }

        // If no protocol is specified, default to https://
        return `https://${url}`;
    }

    return (
        <div>
            <div className={styles.container}>
                <MainNav navbarColor={"#26051C"} />
                {screenLoading ? (
                    <div className="flex items-center justify-center h-screen">
                        <div className="spinner-border text-black" role="status">
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="w-full h-fit flex flex-col">
                            <div className="w-full h-[20vw] border-b-[1px] border-exploreCategoriesBackground relative"
                                style={{
                                    backgroundImage: product?.banner ? `url(${product.banner})` : `url('https://charmhealth-marketplace-assets.s3.us-east-1.amazonaws.com/Homepage/pattern_bg_gray-2.png')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}>

                                <button className="w-[40px] 1.5xl:w-[48px] h-[40px] 1.5xl:h-[48px] bg-[#640444] absolute left-[40px] top-[40px] 1.5xl:left-[24px] 1.5xl:top-[24px] rounded-full flex items-center justify-center opacity-75" onClick={handleGoBack}>
                                    <svg className="mr-1" width="16" height="16" viewBox="0 0 12 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M0.883618 8.59285C0.883618 8.36756 0.987368 8.14232 1.20966 7.9645L10.1018 0.85064C10.5316 0.506818 11.243 0.506818 11.6728 0.85064C12.1026 1.19446 12.1026 1.76358 11.6728 2.10742L3.56609 8.59277L11.6728 15.0781C12.1026 15.422 12.1026 15.9911 11.6728 16.3349C11.243 16.6787 10.5316 16.6787 10.1018 16.3349L1.20966 9.22105C0.987373 9.04322 0.883618 8.81792 0.883618 8.59269V8.59285Z" fill="white" />
                                    </svg>
                                </button>
                            </div>
                            <div className="w-full h-[160px] lg:h-[200px] xl:h-[240px] -mt-[80px] lg:-mt-[100px] xl:-mt-[120px] flex flex-row px-[40px] 1.5xl:px-[104px] 2.5xl:px-[200px] z-20">
                                {product && (
                                    <div
                                        className="w-[160px] h-[160px] lg:w-[200px] lg:h-[200px] xl:w-[240px] xl:h-[240px] border-[1px] border-exploreCategoriesBackground bg-white flex items-center justify-center"
                                    >
                                        <img
                                            src={
                                                product.thumbnail && (product.thumbnail.includes('s3.amazonaws.com') || product.thumbnail.includes('.amazonaws.com'))
                                                    ? product.thumbnail
                                                    : charmhealthlogo_placeholder
                                            }
                                            className="max-w-full max-h-full object-contain"
                                        />
                                    </div>
                                )}

                                {product && (
                                    <div className="mt-[104px] h-[120px] flex-grow">
                                        <SocialLinks product={product} />
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Content after logo, helps with layout */}
                        <div className="w-full px-[40px] 1.5xl:px-[104px] 2.5xl:px-[200px] mx-auto">
                            <div className="mt-[32px] lg:mt-[50px] flex flex-row justify-between items-start">
                                <h2 className="text-[48px] font-[600] font-proximanova text-[#505050] mb-0">{product ? product.name : "No product name available"}</h2>

                                <div className="mt-[12px] hidden 1.5xl:flex flex-row gap-[32px]">
                                    <button className="h-fit w-[212px] text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-10 py-2" onClick={handleButtonClick}>
                                        Submit Request
                                    </button>

                                    {product?.website && product?.website !== "NaN" &&
                                        (<a href={getFormattedUrl(product?.website)} target="_blank" rel="noreferrer">
                                            <button className="h-fit w-[212px] text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-10 py-2">
                                                Visit Website
                                            </button>
                                        </a>)}
                                </div>
                            </div>

                            {isModalOpen && (
                                <SubmitRequestModal
                                    isSignedIn={isSignedIn}
                                    handleSubmit={handleSubmit}
                                    handleCloseModal={handleCloseModal}
                                    handleLogin={handleLogin}
                                    handleSendOtp={handleSendOtp}
                                    setOtpEmail={setOtpEmail}
                                    otpLoading={optLoading}
                                    orderRequestLoading={orderRequestLoading}
                                />
                            )}

                            {isConfirmationModalOpen && (
                                <div className={styles.confirmationModal}>
                                    <div className={styles.confirmationBox}>
                                        <div className={styles.confirmationHeading}>Integration Request Submitted</div>
                                        <div className={styles.confirmationMessage}>
                                            Your integration request has been successfully sent to the vendor. You will receive a response at your email address, {userEmail}.
                                        </div>
                                        <div className="text-center">
                                            <button onClick={() => navigate('/explore/all')} className={styles.returnButton}>
                                                Return to Home
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="w-full mt-[32px] grid grid-cols-3 justify-between">
                                <div className="flex flex-col items-center">
                                    <div className="w-full text-center font-allroundgothic font-[600] text-lg charm-purple mb-[6px] 1.5xl:mb-[12px]">Vendor Name</div>
                                    <div className="w-full h-[60px] flex items-center justify-center">
                                        <div className="w-full text-center font-proximanova font-[600] text-[#505050] text-[24px] 1.5xl:text-[28px]" style={{ lineHeight: '1.20' }}>{product?.vendor ? product.vendor.company_name : "No information available"}</div>
                                    </div>
                                </div>

                                <div className="flex flex-col items-center">
                                    <div className="w-full text-center font-allroundgothic font-[600] text-lg 2xl:text-xl charm-purple mb-[6px] 1.5xl:mb-[12px]">Ratings</div>
                                    <div className="w-full h-[60px] flex items-center justify-center">
                                        <div className="w-full text-center font-proximanova font-[600] text-[#505050] text-[24px] 1.5xl:text-[28px]" style={{ lineHeight: '1.20' }}>
                                            {product?.rating ? `${product.rating}` : "No information available"}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col items-center">
                                    <div className="w-full text-center font-allroundgothic font-[600] text-lg 2xl:text-xl charm-purple mb-[6px] 1.5xl:mb-[12px]">Year Founded</div>
                                    <div className="w-full h-[60px] flex items-center justify-center">
                                        <div className="w-full text-center font-proximanova font-[600] text-[#505050] text-[24px] 1.5xl:text-[28px]" style={{ lineHeight: '1.20' }}>
                                            {product?.vendor ? product.vendor.year_founded : "No information available"}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="1.5xl:hidden mt-[24px] w-full flex flex-row justify-between gap-[16px] xl:gap-[32px]">
                                <button className="w-1/2 text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4" onClick={handleButtonClick}>
                                    Submit Request
                                </button>

                                {product?.website && product?.website !== "NaN" &&
                                    <a href={getFormattedUrl(product?.website)} target="_blank" rel="noreferrer" className="w-1/2">
                                        <button className="w-full text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4">
                                            Visit Website
                                        </button>
                                    </a>
                                }
                            </div>

                            <div className="w-full h-[1px] bg-exploreCategoriesBackground mt-[32px]"></div>

                            <div className="w-full mt-[32px] mx-auto flex flex-col">
                                <h6 className="font-allroundgothic font-[600] text-[22px] mb-0">Product Description</h6>
                                {product?.description &&
                                    <p className="mt-[16px] font-proximanova text-[18px] text-[#505050] mb-0">{product.description}</p>}
                            </div>

                            <div className="w-full mt-[32px] mx-auto flex flex-col">
                                <h6 className="font-allroundgothic font-[600] text-[22px] mb-0">Key Features</h6>
                                {product?.features &&
                                    <p className="mt-[16px] font-proximanova text-[18px] text-[#505050] mb-0">
                                        {product.features?.split('\n').map((line, index) => (
                                            <span key={index}>
                                                {line}
                                                <br />
                                            </span>
                                        ))}
                                    </p>}
                            </div>

                            {product && (
                                <div className="w-full mt-[32px] mx-auto">
                                    <ImageCarousel product={product} />
                                </div>
                            )}

                            <div className="w-full h-[1px] bg-exploreCategoriesBackground mt-[32px]"></div>

                            <div className="mt-[32px] flex flex-col">
                                <h6 className="font-allroundgothic font-[600] text-[22px] mb-0">
                                    Pricing
                                </h6>
                                <div className="mt-[16px] flex flex-row justify-between">
                                    <div className="flex flex-col">
                                        <p className="font-proximanova text-[18px] mb-0">
                                            Free trial: <strong>{product?.has_free_trial ? "Available" : "Not available"}</strong>
                                        </p>
                                        <p className="font-proximanova text-[18px] mb-0">
                                            Free version: <strong>{product?.has_free_version ? "Available" : "Not available"}</strong>
                                        </p>
                                    </div>

                                    {product && product.pricing && (
                                        <button className="w-fit h-fit text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4" onClick={() => { window.open(`${product.pricing}`, '_blank'); }}>View Pricing Page</button>
                                    )}
                                </div>
                            </div>

                            <div className="w-full h-[1px] bg-exploreCategoriesBackground mt-[32px]"></div>

                            {/* <div className="mt-[32px] flex flex-col">
                                <Review product={product} reviews={productReviews} />
                            </div> */}

                            <div className="w-full mt-[32px] mx-auto flex flex-col">
                                <h6 className="font-allroundgothic font-[600] text-[22px] mb-0">
                                    Reviews
                                </h6>
                                {product && productReviews && (
                                    <div className="mt-[16px]">
                                        <Reviews isSignedIn={isSignedIn} product={product} reviews={productReviews} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <div className="w-full bg-marketplaceBackground flex flex-col md:hidden">
                {!product ? (
                    <div className="flex items-center justify-center h-screen">
                        <div className="spinner-border text-black" role="status">
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col">
                        <div className="w-full h-fit flex flex-col">
                            <div className="w-full h-[33.33vw] border-b-[1px] border-exploreCategoriesBackground relative"
                                style={{
                                    backgroundImage: product?.banner ? `url(${product.banner})` : `url('https://charmhealth-marketplace-assets.s3.us-east-1.amazonaws.com/Homepage/pattern_bg_gray-2.png')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}>
                            </div>

                            <button className="w-[36px] h-[36px] bg-[#640444] absolute left-6 top-6 rounded-full flex items-center justify-center opacity-75" onClick={handleGoBack}>
                                <svg className="mr-1" width="12" height="12" viewBox="0 0 12 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0.883618 8.59285C0.883618 8.36756 0.987368 8.14232 1.20966 7.9645L10.1018 0.85064C10.5316 0.506818 11.243 0.506818 11.6728 0.85064C12.1026 1.19446 12.1026 1.76358 11.6728 2.10742L3.56609 8.59277L11.6728 15.0781C12.1026 15.422 12.1026 15.9911 11.6728 16.3349C11.243 16.6787 10.5316 16.6787 10.1018 16.3349L1.20966 9.22105C0.987373 9.04322 0.883618 8.81792 0.883618 8.59269V8.59285Z" fill="white" />
                                </svg>
                            </button>

                            <div className="-mt-[64px] w-full flex flex-row min-h-32 px-6 z-20">
                                <img
                                    src={
                                        product.thumbnail && (product.thumbnail.includes('s3.amazonaws.com') || product.thumbnail.includes('.amazonaws.com'))
                                            ? product.thumbnail
                                            : charmhealthlogo_placeholder
                                    }
                                    className="min-w-32 min-h-32 max-w-32 max-h-32 border-[1px] border-exploreCategoriesBackground"
                                />
                                <div className="flex flex-grow items-end">
                                    <div className="w-full flex flex-row h-16 justify-end items-center gap-[16px] ">
                                        {product && product.facebook && (
                                            <a
                                                href={product.facebook}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="w-9 h-9 xl:w-12 xl:w-12"
                                            >
                                                <img src={"https://charmhealth-marketplace-assets.s3.us-east-1.amazonaws.com/SocialMediaIcons/fb.png"} alt={`Meta Logo`} className="object-cover" />
                                            </a>
                                        )}
                                        {product && product.instagram && (
                                            <a
                                                href={product.instagram}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="w-9 h-9 xl:w-12 xl:w-12"
                                            >
                                                <img src={"https://charmhealth-marketplace-assets.s3.us-east-1.amazonaws.com/SocialMediaIcons/IG.png"} alt={`Instagram Logo`} className="object-cover" />
                                            </a>
                                        )}
                                        {product && product.linkedin && (
                                            <a
                                                href={product.linkedin}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="w-9 h-9 xl:w-12 xl:w-12"
                                            >
                                                <img src={"https://charmhealth-marketplace-assets.s3.us-east-1.amazonaws.com/SocialMediaIcons/LI.png"} alt={`Instagram Logo`} className="object-cover" />
                                            </a>
                                        )}

                                        {product && product.twitter && (
                                            <a
                                                href={product.twitter}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="w-[34px] h-[34px] xl:w-[46px] xl:w-[46px]"
                                            >
                                                <img src={"https://charmhealth-marketplace-assets.s3.us-east-1.amazonaws.com/SocialMediaIcons/x.png"} alt={`Instagram Logo`} className="object-cover" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-[24px] px-6 w-full flex flex-col">
                            <h3 className="font-proximanova text-[26px] font-bold mb-0">
                                {product?.name}
                            </h3>
                            <div className="mt-[10px] w-full grid grid-cols-3 justify-between">
                                <div className="w-full flex flex-col items-center">
                                    <p className="font-allroundgothic text-[10px] font-[600] text-[#9A006A] pt-[6px] mb-0">Vendor Name</p>
                                    <div className="h-[72px] py-[6px] flex items-center justify-center">
                                        <h5 className="font-proximanova text-[16px] font-[600] text-[#505050] text-center mb-0 line-clamp-3" style={{ lineHeight: '1.20' }}>
                                            {product?.vendor ? product.vendor.company_name : "No information available"}
                                        </h5>
                                    </div>
                                </div>
                                <div className="w-full flex flex-col items-center">
                                    <p className="font-allroundgothic text-[10px] font-[600] text-[#9A006A] pt-[6px] mb-0">Rating</p>
                                    <div className="h-[72px] py-[6px] flex items-center justify-center">
                                        <h5 className="font-proximanova text-[16px] font-[600] text-[#505050] text-center mb-0" style={{ lineHeight: '1.20' }}>{product?.rating ? `${product.rating}` : "No reviews yet"}</h5>
                                    </div>
                                </div>
                                <div className="w-full flex flex-col items-center">
                                    <p className="font-allroundgothic text-[10px] font-[600] text-[#9A006A] pt-[6px] mb-0">Year Founded</p>
                                    <div className="h-[72px] py-[6px] flex items-center justify-center">
                                        <h5 className="font-proximanova text-[16px] font-[600] text-[#505050] text-center mb-0" style={{ lineHeight: '1.20' }}> {product?.vendor ? product.vendor.year_founded : "No information available"}</h5>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-[8px] flex flex-row justify-between">
                                <button className="w-[48%] text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova py-2" onClick={handleButtonClick}>Submit Request</button>

                                {product?.website && product?.website !== "NaN" &&
                                    <a href={getFormattedUrl(product?.website)} target="_blank" rel="noreferrer" className="w-[48%] text-center text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova py-2 no-underline">
                                        Visit Website
                                    </a>
                                }
                            </div>

                            <div className="w-full h-[1px] bg-exploreCategoriesBackground mt-[32px]"></div>

                            <div className="mt-[32px] flex flex-col">
                                <h4 className="font-allroundgothic text-[18px] font-[600] mb-0">
                                    Product Description
                                </h4>
                                <p
                                    className={`mt-[24px] font-proximanova text-[18px] mb-0 ${isDescriptionExpanded ? "" : "line-clamp-[10]"
                                        }`}
                                >
                                    {product.description}
                                </p>
                                <button
                                    className="text-link mt-2"
                                    onClick={() => {
                                        setIsDescriptionExpanded(!isDescriptionExpanded);
                                    }}                                >
                                    {isDescriptionExpanded ? "Show Less" : "Show More"}
                                </button>
                            </div>

                            <div className="mt-[32px] flex flex-col">
                                <h4 className="font-allroundgothic text-[18px] font-[600] mb-0">
                                    Key Features
                                </h4>
                                <p
                                    className={`mt-[24px] font-proximanova text-[18px] mb-0 ${isFeaturesExpanded ? "" : "line-clamp-[10]"
                                        }`}
                                >
                                    {product.features?.split('\n').map((line, index) => (
                                        <span key={index}>
                                            {line}
                                            <br />
                                        </span>
                                    ))}
                                </p>
                                <button
                                    className="text-link mt-2"
                                    onClick={() => {
                                        setIsFeaturesExpanded(!isFeaturesExpanded);
                                    }}                                >
                                    {isFeaturesExpanded ? "Show Less" : "Show More"}
                                </button>
                            </div>

                            <div className="mt-[32px]">
                                <ImageCarousel product={product} />
                            </div>

                            <div className="w-full h-[1px] bg-exploreCategoriesBackground mt-[32px]"></div>

                            <div className="mt-[32px] flex flex-col">
                                <h4 className="font-allroundgothic text-[18px] font-[600] mb-0">
                                    Pricing
                                </h4>

                                <p className="mt-[24px] font-proximanova text-[18px] mb-0">
                                    Free trial: <strong>{product?.has_free_trial ? "Available" : "Not available"}</strong>
                                </p>
                                <p className="font-proximanova text-[18px] mb-0">
                                    Free version: <strong>{product?.has_free_version ? "Available" : "Not available"}</strong>
                                </p>

                                {product && product.pricing && (
                                    <button className="mt-[24px] w-fit text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4" onClick={() => { window.open(`${product.pricing}`, '_blank'); }}>View Pricing Page</button>
                                )}
                            </div>

                            <div className="w-full h-[1px] bg-exploreCategoriesBackground mt-[32px]"></div>

                            <div className="mt-[32px] flex flex-col">
                                <h4 className="font-allroundgothic text-[18px] font-[600] mb-0">
                                    Reviews
                                </h4>
                                <Reviews isSignedIn={isSignedIn} product={product} reviews={productReviews} />
                            </div>
                            {isModalOpen && (
                                <SubmitRequestModal
                                    isSignedIn={isSignedIn}
                                    handleSubmit={handleSubmit}
                                    handleCloseModal={handleCloseModal}
                                    handleLogin={handleLogin}
                                    handleSendOtp={handleSendOtp}
                                    setOtpEmail={setOtpEmail}
                                    otpLoading={optLoading}
                                    orderRequestLoading={orderRequestLoading}
                                />
                            )}

                            {isConfirmationModalOpen && (
                                <div className={styles.confirmationModal}>
                                    <div className={styles.confirmationBox}>
                                        <div className={styles.confirmationHeading}>Integration Request Submitted</div>
                                        <div className={styles.confirmationMessage}>
                                            Your integration request has been successfully sent to the vendor. You will receive a response at your email address, {userEmail}.
                                        </div>
                                        <div className="text-center">
                                            <button onClick={() => navigate('/explore/all')} className={styles.returnButton}>
                                                Return to Home
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className="pt-[32px] bg-marketplaceBackground">
                <Legal />
            </div>
        </div >

    );
};

export default DetailedListing;